import { useTranslation } from "@redotech/redo-i18n";
import type { ReturnProcessingDecisionState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  DecisionLabelColor,
  DEFAULT_FALSE_LABEL_COLOR,
  DEFAULT_TRUE_LABEL_COLOR,
} from "@redotech/redo-return-core-common/return-flow/decision-label-color";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode } from "react";
import { DecisionLabelColorSelect } from "./decision-label-color-select";
import { StepImageUpload } from "./step-image-upload";

export const ReturnProcessingDecisionDetails = memo(
  function ReturnProcessingDecisionDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<ReturnProcessingDecisionState>) {
    const { t } = useTranslation();
    return (
      <Flex dir="column" gap="xl">
        <RedoTextareaInputField
          containerHeight="72px"
          fullWidth
          label={t("Prompt")}
          placeholder={t("Question shown to the warehouse worker")}
          setValue={(prompt) => setState((state) => ({ ...state, prompt }))}
          value={state.prompt}
        />
        <StepImageUpload
          imageUrl={state.imageUrl}
          setImageUrl={(imageUrl) =>
            setState((state) => ({ ...state, imageUrl }))
          }
        />
        <DecisionBranch
          buttonLabel={state.trueLabel}
          buttonLabelPlaceholder={t("Yes")}
          color={state.trueLabelColor ?? DEFAULT_TRUE_LABEL_COLOR}
          nextStep={stepSelect({
            value: state.nextTrue,
            valueChange: (nextTrue) =>
              setState((state) => ({ ...state, nextTrue })),
            handleIndex: 0,
          })}
          setButtonLabel={(trueLabel) =>
            setState((state) => ({ ...state, trueLabel }))
          }
          setColor={(trueLabelColor) =>
            setState((state) => ({ ...state, trueLabelColor }))
          }
          title={t("True branch")}
        />
        <DecisionBranch
          buttonLabel={state.falseLabel}
          buttonLabelPlaceholder={t("No")}
          color={state.falseLabelColor ?? DEFAULT_FALSE_LABEL_COLOR}
          nextStep={stepSelect({
            value: state.nextFalse,
            valueChange: (nextFalse) =>
              setState((state) => ({ ...state, nextFalse })),
            handleIndex: 1,
          })}
          setButtonLabel={(falseLabel) =>
            setState((state) => ({ ...state, falseLabel }))
          }
          setColor={(falseLabelColor) =>
            setState((state) => ({ ...state, falseLabelColor }))
          }
          title={t("False branch")}
        />
      </Flex>
    );
  },
);

const DecisionBranch = memo(function DecisionBranch({
  title,
  buttonLabel,
  buttonLabelPlaceholder,
  setButtonLabel,
  color,
  setColor,
  nextStep,
}: {
  title: string;
  buttonLabel: string;
  buttonLabelPlaceholder: string;
  setButtonLabel: (label: string) => void;
  color: DecisionLabelColor;
  setColor: (color: DecisionLabelColor) => void;
  nextStep: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="lg">
      <Divider />
      <Text fontSize="xs" fontWeight="semibold">
        {title}
      </Text>
      <Flex align="flex-end" gap="sm">
        <Flex grow={1} minW="none">
          <RedoTextInput
            fullWidth
            label={t("Button label")}
            placeholder={buttonLabelPlaceholder}
            setValue={setButtonLabel}
            value={buttonLabel}
          />
        </Flex>
        <DecisionLabelColorSelect
          label={t("Color")}
          onChange={setColor}
          value={color}
        />
      </Flex>
      <Flex dir="column" gap="sm">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next step")}
        </Text>
        {nextStep}
      </Flex>
    </Flex>
  );
});
